import { useMemo, useState } from "react";
import ReactSelect, { type StylesConfig } from "react-select";

type SitePickerOption = {
  value: string;
  label: string;
  code: string;
  navisionCode: string;
  innCode: string;
  searchText: string;
  siteName: string;
};

export type SitePickerSite = {
  slug: string;
  code: string;
  innCode?: string | null;
  navisionCode?: string | null;
  friendlyName: string;
};

function fuzzyIncludes(needle: string, haystack: string): boolean {
  if (!needle) return true;
  const n = needle.toLowerCase().trim();
  const h = haystack.toLowerCase();
  if (h.includes(n)) return true;
  let i = 0;
  for (const c of h) {
    if (c === n[i]) i += 1;
    if (i === n.length) return true;
  }
  return false;
}

export function SitePickerInline({
  inputId,
  value,
  sites,
  onChange,
}: {
  inputId?: string;
  value: string;
  sites: SitePickerSite[];
  onChange: (slug: string) => void;
}) {
  const options = useMemo<SitePickerOption[]>(
    () =>
      sites.map((site) => ({
        value: site.slug,
        label: `${site.friendlyName} (${site.innCode ?? site.code})`,
        code: site.code,
        navisionCode: site.navisionCode ?? "",
        innCode: site.innCode ?? site.code,
        siteName: site.friendlyName,
        searchText: `${site.slug} ${site.code} ${site.innCode ?? ""} ${site.navisionCode ?? ""} ${site.friendlyName}`,
      })),
    [sites],
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const selected = options.find((site) => site.value === value) ?? null;
  const styles: StylesConfig<SitePickerOption> = {
    control: (base, state) => ({
      ...base,
      minHeight: 40,
      height: 40,
      borderRadius: 12,
      backgroundColor: "rgb(var(--surface))",
      borderColor: state.isFocused
        ? "rgb(var(--violet) / 0.55)"
        : "rgb(var(--line))",
      boxShadow: "none",
    }),
    valueContainer: (base) => ({
      ...base,
      height: 40,
      paddingTop: 0,
      paddingBottom: 0,
      outline: "none",
      boxShadow: "none",
      textShadow: "none",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: 40,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 60 }),
    menu: (base) => ({
      ...base,
      borderRadius: 12,
      border: "1px solid rgb(var(--line))",
      backgroundColor: "rgb(var(--surface-2))",
    }),
    input: (base) => ({
      ...base,
      color: "rgb(var(--fg))",
      outline: "none",
      boxShadow: "none",
      textShadow: "none",
    }),
    singleValue: (base) => ({ ...base, color: "rgb(var(--fg))" }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused
        ? "rgb(var(--surface-tint) / 0.8)"
        : "transparent",
      color: "rgb(var(--fg))",
    }),
  };

  return (
    <ReactSelect<SitePickerOption>
      inputId={inputId}
      value={selected}
      options={options}
      onChange={(next) => {
        if (next) onChange(next.value);
      }}
      filterOption={(candidate, input) => {
        const query = input.trim();
        if (!query) return true;
        return fuzzyIncludes(query, candidate.data.searchText);
      }}
      getOptionLabel={(option) => option.label}
      getOptionValue={(option) => option.value}
      formatOptionLabel={(option) => {
        const selectedOption = option;
        return (
          <div className="flex flex-col py-0.5">
            <span className="text-sm text-fg">
              {selectedOption.siteName} ({selectedOption.innCode})
            </span>
            <span className="font-mono text-[10.5px] text-fg-mute">
              {selectedOption.navisionCode
                ? `NAV ${selectedOption.navisionCode}`
                : "NAV -"}
            </span>
          </div>
        );
      }}
      placeholder="Select site"
      noOptionsMessage={() => "No matching sites"}
      styles={styles}
      menuPortalTarget={document.body}
      classNamePrefix="site-picker"
      isSearchable
      inputValue={inputValue}
      onInputChange={(next: string, meta: { action: string }) => {
        if (meta.action === "input-change") setInputValue(next);
        if (meta.action === "menu-close") setInputValue("");
        return next;
      }}
      onMenuOpen={() => setMenuOpen(true)}
      onMenuClose={() => {
        setMenuOpen(false);
        setInputValue("");
      }}
      controlShouldRenderValue={!(menuOpen && inputValue.trim() === "")}
      menuShouldBlockScroll
    />
  );
}
